import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Rating as MuiRating,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DocumentData, DocumentSnapshot, Timestamp } from 'firebase/firestore';
import { Driver, DriverHistorySummary, DriverShift, Order, OrderHistoryFilters, Rating } from '../models/Driver';
import { PaginatedDriverOrderHistoryResult, PaginatedDriverPaidShiftsResult } from '../services/driverService';

interface DriverDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  driver: Driver | null;
  fetchHistoryPage: (
    driverId: string,
    filters?: OrderHistoryFilters,
    options?: {
      pageSize?: number;
      startAfterDocument?: DocumentSnapshot<DocumentData> | null;
    }
  ) => Promise<PaginatedDriverOrderHistoryResult>;
  fetchHistorySummary: (
    driverId: string,
    filters?: OrderHistoryFilters
  ) => Promise<DriverHistorySummary>;
  fetchRatingsForOrderIds: (orderIds: string[]) => Promise<Record<string, Rating>>;
  fetchOpenShift: (driverId: string) => Promise<DriverShift | null>;
  fetchPaidShiftsPage: (
    driverId: string,
    filters?: OrderHistoryFilters,
    options?: {
      pageSize?: number;
      startAfterDocument?: DocumentSnapshot<DocumentData> | null;
    }
  ) => Promise<PaginatedDriverPaidShiftsResult>;
  onResetCounter: (
    driverId: string,
    counterField: 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count'
  ) => Promise<void>;
}

type HistoryTab = 'orders' | 'shifts';
type PeriodFilter = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'custom';

const DEFAULT_PERIOD_FILTER: PeriodFilter = 'today';

const ORDERS_PAGE_SIZE = 25;
const PAID_SHIFTS_PAGE_SIZE = 10;

const currencyFormatter = new Intl.NumberFormat('fr-MA', {
  style: 'currency',
  currency: 'MAD',
  minimumFractionDigits: 2,
});

const orderStatusMeta: Record<
  string,
  {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  }
> = {
  PLACED: { label: 'Placée', color: 'default' },
  ACCEPTED: { label: 'Acceptée', color: 'info' },
  PREPARING: { label: 'En préparation', color: 'warning' },
  READY_FOR_PICKUP: { label: 'Prête', color: 'warning' },
  ACCEPTED_BY_DRIVER: { label: 'Acceptée par livreur', color: 'info' },
  DELIVERING: { label: 'En livraison', color: 'primary' },
  DELIVERED: { label: 'Livrée', color: 'success' },
  CANCELLED: { label: 'Annulée', color: 'error' },
  NO_DRIVER_FOUND: { label: 'Sans livreur', color: 'secondary' },
};

const DriverDetailsDialog: React.FC<DriverDetailsDialogProps> = ({
  open,
  onClose,
  driver,
  fetchHistoryPage,
  fetchHistorySummary,
  fetchRatingsForOrderIds,
  fetchOpenShift,
  fetchPaidShiftsPage,
  onResetCounter,
}) => {
  const [history, setHistory] = useState<Order[]>([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<{
    field: 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count';
    label: string;
  } | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [ratingsMap, setRatingsMap] = useState<Record<string, Rating>>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [ratingsError, setRatingsError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [openShift, setOpenShift] = useState<DriverShift | null>(null);
  const [paidShifts, setPaidShifts] = useState<DriverShift[]>([]);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [paidShiftsLoading, setPaidShiftsLoading] = useState(false);
  const [paidShiftsError, setPaidShiftsError] = useState<string | null>(null);
  const [historyTab, setHistoryTab] = useState<HistoryTab>('orders');
  const [historySummary, setHistorySummary] = useState<DriverHistorySummary | null>(null);
  const [currentOrdersPage, setCurrentOrdersPage] = useState(0);
  const [currentPaidShiftsPage, setCurrentPaidShiftsPage] = useState(0);
  const [orderPageCursors, setOrderPageCursors] = useState<Array<DocumentSnapshot<DocumentData> | null>>([null]);
  const [paidShiftPageCursors, setPaidShiftPageCursors] = useState<Array<DocumentSnapshot<DocumentData> | null>>([null]);
  const [hasMoreOrdersPages, setHasMoreOrdersPages] = useState(false);
  const [hasMorePaidShiftsPages, setHasMorePaidShiftsPages] = useState(false);

  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(DEFAULT_PERIOD_FILTER);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appliedPeriodFilter, setAppliedPeriodFilter] = useState<PeriodFilter>(DEFAULT_PERIOD_FILTER);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);

  const formatTimestamp = (timestamp?: Timestamp): string => {
    if (!timestamp) {
      return 'N/A';
    }

    return `${timestamp.toDate().toLocaleDateString()} ${timestamp.toDate().toLocaleTimeString()}`;
  };

  const formatAmount = (value?: number): string => {
    return currencyFormatter.format(Number.isFinite(value ?? 0) ? value ?? 0 : 0);
  };

  const getDriverAmountForOrder = (order: Order, fallbackShift?: DriverShift | null): number => {
    if (typeof order.driverCommissionAmount === 'number') {
      return order.driverCommissionAmount;
    }

    const percentage =
      order.driverCommissionPercentageSnapshot ??
      fallbackShift?.driverCommissionPercentageSnapshot ??
      driver?.driverPourcentage ??
      0;

    return (order.realDeliveryPrice ?? 0) * (percentage / 100);
  };

  const isCashOrder = (order: Order): boolean => {
    return order.paymentMethod === 'cash';
  };

  const getStartOfDay = (date: Date): Date => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const buildHistoryFilters = (
    activePeriod: PeriodFilter = appliedPeriodFilter,
    activeStartDate: Date | null = appliedStartDate,
    activeEndDate: Date | null = appliedEndDate
  ): OrderHistoryFilters => {
    if (activePeriod === 'custom' && activeStartDate && activeEndDate) {
      return {
        dateRange: {
          startDate: activeStartDate,
          endDate: activeEndDate,
        },
      };
    }

    const today = getStartOfDay(new Date());

    switch (activePeriod) {
      case 'today':
        return { period: 'day' };
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          dateRange: {
            startDate: yesterday,
            endDate: yesterday,
          },
        };
      }
      case 'last7Days': {
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 6);
        return {
          dateRange: {
            startDate,
            endDate: new Date(today),
          },
        };
      }
      case 'last30Days': {
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 29);
        return {
          dateRange: {
            startDate,
            endDate: new Date(today),
          },
        };
      }
      case 'custom':
      default:
        return { period: 'day' };
    }
  };

  const fetchOrderHistoryPage = async (
    pageIndex: number,
    activePeriod: PeriodFilter = appliedPeriodFilter,
    activeStartDate: Date | null = appliedStartDate,
    activeEndDate: Date | null = appliedEndDate,
    pageCursors: Array<DocumentSnapshot<DocumentData> | null> = orderPageCursors
  ) => {
    if (!driver) {
      return;
    }

    setHistoryLoading(true);
    setRatingsLoading(true);
    setHistoryError(null);
    setRatingsError(null);
    setRatingsMap({});

    try {
      const page = await fetchHistoryPage(
        driver.id,
        buildHistoryFilters(activePeriod, activeStartDate, activeEndDate),
        {
          pageSize: ORDERS_PAGE_SIZE,
          startAfterDocument: pageCursors[pageIndex] ?? null,
        }
      );

      let pageRatings: Record<string, Rating> = {};
      let pageRatingsError: string | null = null;

      try {
        pageRatings = await fetchRatingsForOrderIds(page.orders.map((order) => order.id));
      } catch (err: any) {
        pageRatingsError = err.message;
      }

      setHistory(page.orders);
      setRatingsMap(pageRatings);
      setRatingsError(pageRatingsError);
      setCurrentOrdersPage(pageIndex);
      setHasMoreOrdersPages(page.hasMore);

      setOrderPageCursors((current) => {
        const next = current.slice(0, Math.max(current.length, pageIndex + 1));
        while (next.length < pageIndex + 1) {
          next.push(null);
        }

        if (page.hasMore && page.lastDocument) {
          if (next.length === pageIndex + 1) {
            next.push(page.lastDocument);
          } else {
            next[pageIndex + 1] = page.lastDocument;
          }
        } else if (next.length > pageIndex + 1) {
          next.splice(pageIndex + 1);
        }

        return next;
      });
    } catch (err: any) {
      setHistoryError(err.message);
      console.error('Error fetching driver history:', err);
    } finally {
      setHistoryLoading(false);
      setRatingsLoading(false);
    }
  };

  const fetchHistorySummaryData = async (
    activePeriod: PeriodFilter = appliedPeriodFilter,
    activeStartDate: Date | null = appliedStartDate,
    activeEndDate: Date | null = appliedEndDate
  ) => {
    if (!driver) {
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);

    try {
      const summary = await fetchHistorySummary(
        driver.id,
        buildHistoryFilters(activePeriod, activeStartDate, activeEndDate)
      );
      setHistorySummary(summary);
    } catch (err: any) {
      setSummaryError(err.message);
      console.error('Error fetching driver history summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchOpenShiftData = async () => {
    if (!driver) {
      return;
    }

    setShiftLoading(true);
    setShiftError(null);

    try {
      const currentOpenShift = await fetchOpenShift(driver.id);
      setOpenShift(currentOpenShift);
    } catch (err: any) {
      setShiftError(err.message);
      console.error('Error fetching open driver shift:', err);
    } finally {
      setShiftLoading(false);
    }
  };

  const fetchPaidShiftsPageData = async (
    pageIndex: number,
    activePeriod: PeriodFilter = appliedPeriodFilter,
    activeStartDate: Date | null = appliedStartDate,
    activeEndDate: Date | null = appliedEndDate,
    pageCursors: Array<DocumentSnapshot<DocumentData> | null> = paidShiftPageCursors
  ) => {
    if (!driver) {
      return;
    }

    setPaidShiftsLoading(true);
    setPaidShiftsError(null);

    try {
      const page = await fetchPaidShiftsPage(
        driver.id,
        buildHistoryFilters(activePeriod, activeStartDate, activeEndDate),
        {
          pageSize: PAID_SHIFTS_PAGE_SIZE,
          startAfterDocument: pageCursors[pageIndex] ?? null,
        }
      );

      setPaidShifts(page.shifts);
      setCurrentPaidShiftsPage(pageIndex);
      setHasMorePaidShiftsPages(page.hasMore);

      setPaidShiftPageCursors((current) => {
        const next = current.slice(0, Math.max(current.length, pageIndex + 1));
        while (next.length < pageIndex + 1) {
          next.push(null);
        }

        if (page.hasMore && page.lastDocument) {
          if (next.length === pageIndex + 1) {
            next.push(page.lastDocument);
          } else {
            next[pageIndex + 1] = page.lastDocument;
          }
        } else if (next.length > pageIndex + 1) {
          next.splice(pageIndex + 1);
        }

        return next;
      });
    } catch (err: any) {
      setPaidShiftsError(err.message);
      console.error('Error fetching paid driver shifts page:', err);
    } finally {
      setPaidShiftsLoading(false);
    }
  };

  const refreshHistoryData = async (
    activePeriod: PeriodFilter = appliedPeriodFilter,
    activeStartDate: Date | null = appliedStartDate,
    activeEndDate: Date | null = appliedEndDate
  ) => {
    const initialOrderCursors: Array<DocumentSnapshot<DocumentData> | null> = [null];
    const initialShiftCursors: Array<DocumentSnapshot<DocumentData> | null> = [null];
    setCurrentOrdersPage(0);
    setCurrentPaidShiftsPage(0);
    setOrderPageCursors(initialOrderCursors);
    setPaidShiftPageCursors(initialShiftCursors);

    await Promise.all([
      fetchHistorySummaryData(activePeriod, activeStartDate, activeEndDate),
      fetchOrderHistoryPage(0, activePeriod, activeStartDate, activeEndDate, initialOrderCursors),
      fetchPaidShiftsPageData(0, activePeriod, activeStartDate, activeEndDate, initialShiftCursors),
    ]);
  };

  useEffect(() => {
    if (open && driver) {
      setHistoryTab('orders');
      setPeriodFilter(DEFAULT_PERIOD_FILTER);
      setStartDate(null);
      setEndDate(null);
      setAppliedPeriodFilter(DEFAULT_PERIOD_FILTER);
      setAppliedStartDate(null);
      setAppliedEndDate(null);
      setHistorySummary(null);
      setCurrentOrdersPage(0);
      setCurrentPaidShiftsPage(0);
      setOrderPageCursors([null]);
      setPaidShiftPageCursors([null]);
      setHasMoreOrdersPages(false);
      setHasMorePaidShiftsPages(false);
      refreshHistoryData(DEFAULT_PERIOD_FILTER, null, null);
      fetchOpenShiftData();
    } else {
      setHistory([]);
      setRatingsMap({});
      setHistoryError(null);
      setRatingsError(null);
      setSummaryError(null);
      setOpenShift(null);
      setPaidShifts([]);
      setShiftError(null);
      setHistoryTab('orders');
      setHistorySummary(null);
      setCurrentOrdersPage(0);
      setCurrentPaidShiftsPage(0);
      setOrderPageCursors([null]);
      setPaidShiftPageCursors([null]);
      setHasMoreOrdersPages(false);
      setHasMorePaidShiftsPages(false);
      setPeriodFilter(DEFAULT_PERIOD_FILTER);
      setStartDate(null);
      setEndDate(null);
      setAppliedPeriodFilter(DEFAULT_PERIOD_FILTER);
      setAppliedStartDate(null);
      setAppliedEndDate(null);
      setPaidShiftsError(null);
    }
  }, [open, driver]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as PeriodFilter;
    setPeriodFilter(newValue);

    if (newValue !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleApplyFilters = () => {
    setAppliedPeriodFilter(periodFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    void refreshHistoryData(periodFilter, startDate, endDate);
  };

  const openResetDialog = (target: {
    field: 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count';
    label: string;
  }) => {
    setResetError(null);
    setResetTarget(target);
    setResetDialogOpen(true);
  };

  const closeResetDialog = () => {
    setResetDialogOpen(false);
    setResetTarget(null);
    setResetLoading(false);
    setResetError(null);
  };

  const handleConfirmReset = async () => {
    if (!driver || !resetTarget) {
      return;
    }

    try {
      setResetLoading(true);
      setResetError(null);
      await onResetCounter(driver.id, resetTarget.field);
      closeResetDialog();
    } catch (e: any) {
      setResetError(e?.message || 'Erreur lors de la réinitialisation.');
      setResetLoading(false);
    }
  };

  if (!driver) {
    return null;
  }

  const totalOrdersCount = historySummary?.ordersSummary.totalOrders ?? 0;
  const totalOrdersPages =
    historySummary == null
      ? history.length === 0
        ? 0
        : hasMoreOrdersPages
          ? currentOrdersPage + 2
          : currentOrdersPage + 1
      : totalOrdersCount === 0
        ? 0
        : Math.ceil(totalOrdersCount / ORDERS_PAGE_SIZE);

  const totalPaidShiftsCount = historySummary?.paidShiftsSummary.totalShifts ?? 0;
  const totalPaidShiftsPages =
    historySummary == null
      ? paidShifts.length === 0
        ? 0
        : hasMorePaidShiftsPages
          ? currentPaidShiftsPage + 2
          : currentPaidShiftsPage + 1
      : totalPaidShiftsCount === 0
        ? 0
        : Math.ceil(totalPaidShiftsCount / PAID_SHIFTS_PAGE_SIZE);

  const buildMetricCard = (label: string, value: string, colors: { bg: string; fg: string }) => (
    <Box
      sx={{
        minWidth: 160,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: colors.bg,
        bgcolor: colors.bg,
      }}
    >
      <Typography variant="caption" sx={{ color: colors.fg, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );

  const buildInfoPill = (label: string, value: string) => (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 999,
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );

  const renderPageControls = ({
    currentPage,
    totalPages,
    onPrevious,
    onNext,
  }: {
    currentPage: number;
    totalPages: number;
    onPrevious?: () => void;
    onNext?: () => void;
  }) => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          disabled={!onPrevious}
          onClick={onPrevious}
        >
          Précédent
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={!onNext}
          onClick={onNext}
        >
          Suivant
        </Button>
      </Box>
    );
  };

  const renderOrderHistoryCard = (order: Order) => {
    const orderRating = ratingsMap[order.id];
    const statusMeta = orderStatusMeta[order.status] ?? {
      label: order.status,
      color: 'default' as const,
    };

    return (
      <Paper
        key={order.id}
        variant="outlined"
        sx={{ p: 2, borderRadius: 3, borderColor: 'divider' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{`Commande ${order.id}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTimestamp(order.createdAt)}
            </Typography>
          </Box>
          <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
        </Box>

        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {buildInfoPill('Montant commande', formatAmount(order.totalAmount))}
          {buildInfoPill('À payer livreur', formatAmount(getDriverAmountForOrder(order, openShift)))}
          {buildInfoPill('Paiement', isCashOrder(order) ? 'Espèces' : 'Carte')}
        </Box>

        <Box sx={{ mt: 1.5 }}>
          {orderRating ? (
            <Box>
              <MuiRating value={orderRating.rating} precision={0.5} readOnly size="small" />
              {orderRating.review_text && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {orderRating.review_text}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucun avis client.
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  const renderPaidShiftHistoryCard = (shift: DriverShift) => (
    <Paper
      key={shift.id}
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, borderColor: 'divider' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Shift payé</Typography>
          <Typography variant="body2" color="text.secondary">
            {shift.paidClosedAt ? `Réglé le ${formatTimestamp(shift.paidClosedAt)}` : 'Shift réglé'}
          </Typography>
        </Box>
        <Chip
          label={shift.startedAt ? `Ouvert le ${formatTimestamp(shift.startedAt)}` : 'Date indisponible'}
          variant="outlined"
          size="small"
        />
      </Box>

      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {buildInfoPill('Commandes', `${shift.totalOrders}`)}
        {buildInfoPill('Total payé', formatAmount(shift.totalCommission))}
        {buildInfoPill('Espèces collectées', formatAmount(shift.totalCashCollected))}
      </Box>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{`Détails du livreur : ${driver.firstName} ${driver.lastName}`}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Informations Générales
          </Typography>
          <Typography>
            <strong>Téléphone :</strong> {driver.phone}
          </Typography>
          <Typography>
            <strong>Email :</strong> {driver.email ?? 'N/A'}
          </Typography>
          <Typography>
            <strong>CIN :</strong> {driver.cin ?? 'N/A'}
          </Typography>
          <Typography>
            <strong>Adresse :</strong> {driver.address ?? 'N/A'}
          </Typography>
          <Typography>
            <strong>Type de Véhicule :</strong> {driver.vehicleType ?? 'N/A'}
          </Typography>
          <Typography>
            <strong>Immatriculation du Véhicule :</strong> {driver.vehicleRegistration ?? 'N/A'}
          </Typography>
          <Typography>
            <strong>Statut :</strong> {driver.activityStatus ?? 'N/A'}
          </Typography>
          <Typography>
            <strong>Activé :</strong> {driver.enabled ? 'Oui' : 'Non'}
          </Typography>
          <Typography>
            <strong>Favori :</strong> {driver.is_favorite ? 'Oui' : 'Non'}
          </Typography>
          <Typography>
            <strong>Commission :</strong> {driver.driverPourcentage ?? 'N/A'}%
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Résumé des Performances
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            <strong>Note Moyenne :</strong>{' '}
            {typeof driver.rating === 'number' ? driver.rating.toFixed(1) : '0.0'} ({driver.review_count} avis)
          </Typography>

          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <Typography>
              <strong>Commandes Acceptées :</strong> {driver.orders_accepted_count}
            </Typography>
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                openResetDialog({ field: 'orders_accepted_count', label: 'Commandes Acceptées' })
              }
              title="Réinitialiser"
              sx={{ ml: 0.25 }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <Typography>
              <strong>Offres Ignorées :</strong> {driver.orders_ignored_count}
            </Typography>
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                openResetDialog({ field: 'orders_ignored_count', label: 'Offres Ignorées' })
              }
              title="Réinitialiser"
              sx={{ ml: 0.25 }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography>
              <strong>Offres Refusées :</strong> {driver.orders_refused_count}
            </Typography>
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                openResetDialog({ field: 'orders_refused_count', label: 'Offres Refusées' })
              }
              title="Réinitialiser"
              sx={{ ml: 0.25 }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Shift Financier
          </Typography>
          {shiftLoading ? (
            <CircularProgress size={24} />
          ) : shiftError ? (
            <Alert severity="error">{shiftError}</Alert>
          ) : openShift ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>Shift en cours</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {openShift.startedAt
                      ? `Démarré le ${formatTimestamp(openShift.startedAt)}`
                      : 'Démarrage en cours de synchronisation'}
                  </Typography>
                </Box>
                <Chip
                  label={openShift.settlementProcessing ? 'Règlement en cours' : 'Ouvert'}
                  color={openShift.settlementProcessing ? 'warning' : 'success'}
                  size="small"
                />
              </Box>

              <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {buildMetricCard('Commandes', `${openShift.totalOrders}`, {
                  bg: '#edf7ed',
                  fg: '#1b5e20',
                })}
                {buildMetricCard('À payer', formatAmount(openShift.totalCommission), {
                  bg: '#e3f2fd',
                  fg: '#0d47a1',
                })}
                {buildMetricCard(
                  'Espèces collectées',
                  formatAmount(openShift.totalCashCollected),
                  {
                    bg: '#fff3e0',
                    fg: '#e65100',
                  }
                )}
              </Box>
            </Paper>
          ) : (
            <Typography>Aucun shift ouvert pour ce livreur.</Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" gutterBottom>
            Historique livreur
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Consultez les commandes et les shifts déjà réglés sans surcharger l’écran.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant={historyTab === 'orders' ? 'contained' : 'outlined'}
              onClick={() => setHistoryTab('orders')}
            >
              Commandes
            </Button>
            <Button
              variant={historyTab === 'shifts' ? 'contained' : 'outlined'}
              onClick={() => setHistoryTab('shifts')}
            >
              Shifts payés
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
              Période
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="period-filter-label">Période</InputLabel>
                <Select
                  labelId="period-filter-label"
                  id="period-filter"
                  value={periodFilter}
                  label="Période"
                  onChange={handlePeriodChange}
                >
                  <MenuItem value="today">Aujourd&apos;hui</MenuItem>
                  <MenuItem value="yesterday">Hier</MenuItem>
                  <MenuItem value="last7Days">7 derniers jours</MenuItem>
                  <MenuItem value="last30Days">30 derniers jours</MenuItem>
                  <MenuItem value="custom">Période personnalisée</MenuItem>
                </Select>
              </FormControl>

              {periodFilter === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date de début"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              )}

              {periodFilter === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date de fin"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={periodFilter === 'custom' && (!startDate || !endDate)}
              >
                Appliquer les filtres
              </Button>
            </Box>
          </Paper>

          {historyTab === 'orders' ? (
            <>
              {historyLoading || ratingsLoading || summaryLoading ? (
                <CircularProgress size={24} />
              ) : historyError ? (
                <Alert severity="error">Erreur lors du chargement de l&apos;historique : {historyError}</Alert>
              ) : (
                <>
                  {summaryError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Résumé indisponible : {summaryError}
                    </Alert>
                  )}
                  {ratingsError && <Alert severity="warning" sx={{ mb: 2 }}>{ratingsError}</Alert>}

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {buildMetricCard('Commandes', `${historySummary?.ordersSummary.totalOrders ?? 0}`, {
                      bg: '#edf7ed',
                      fg: '#1b5e20',
                    })}
                    {buildMetricCard(
                      'Montant commandes',
                      formatAmount(historySummary?.ordersSummary.totalOrdersAmount ?? 0),
                      {
                      bg: '#e3f2fd',
                      fg: '#0d47a1',
                    })}
                    {buildMetricCard(
                      'À payer livreur',
                      formatAmount(historySummary?.ordersSummary.totalDriverPayment ?? 0),
                      {
                      bg: '#ede7f6',
                      fg: '#4527a0',
                    })}
                    {buildMetricCard(
                      'Espèces collectées',
                      formatAmount(historySummary?.ordersSummary.totalCashCollected ?? 0),
                      {
                      bg: '#fff3e0',
                      fg: '#e65100',
                    })}
                  </Box>

                  {history.length === 0 ? (
                    <Typography>Aucun historique de commande disponible.</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {`Page ${currentOrdersPage + 1}${totalOrdersPages > 0 ? ` sur ${totalOrdersPages}` : ''}`}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {history.map((order) => renderOrderHistoryCard(order))}
                      </Box>
                      {renderPageControls({
                        currentPage: currentOrdersPage,
                        totalPages: totalOrdersPages,
                        onPrevious:
                          currentOrdersPage > 0
                            ? () => {
                                void fetchOrderHistoryPage(currentOrdersPage - 1);
                              }
                            : undefined,
                        onNext:
                          hasMoreOrdersPages
                            ? () => {
                                void fetchOrderHistoryPage(currentOrdersPage + 1);
                              }
                            : undefined,
                      })}
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {paidShiftsLoading || summaryLoading ? (
                <CircularProgress size={24} />
              ) : paidShiftsError ? (
                <Alert severity="error">{paidShiftsError}</Alert>
              ) : (
                <>
                  {summaryError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Résumé indisponible : {summaryError}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {buildMetricCard('Shifts', `${historySummary?.paidShiftsSummary.totalShifts ?? 0}`, {
                      bg: '#edf7ed',
                      fg: '#1b5e20',
                    })}
                    {buildMetricCard('Commandes', `${historySummary?.paidShiftsSummary.totalOrders ?? 0}`, {
                      bg: '#e3f2fd',
                      fg: '#0d47a1',
                    })}
                    {buildMetricCard(
                      'Total payé',
                      formatAmount(historySummary?.paidShiftsSummary.totalPaidAmount ?? 0),
                      {
                      bg: '#ede7f6',
                      fg: '#4527a0',
                    })}
                    {buildMetricCard(
                      'Espèces collectées',
                      formatAmount(historySummary?.paidShiftsSummary.totalCashCollected ?? 0),
                      {
                      bg: '#fff3e0',
                      fg: '#e65100',
                    })}
                  </Box>

                  {paidShifts.length === 0 ? (
                    <Typography>Aucun shift payé pour ce livreur.</Typography>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {`Page ${currentPaidShiftsPage + 1}${totalPaidShiftsPages > 0 ? ` sur ${totalPaidShiftsPages}` : ''}`}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {paidShifts.map((shift) => renderPaidShiftHistoryCard(shift))}
                      </Box>
                      {renderPageControls({
                        currentPage: currentPaidShiftsPage,
                        totalPages: totalPaidShiftsPages,
                        onPrevious:
                          currentPaidShiftsPage > 0
                            ? () => {
                                void fetchPaidShiftsPageData(currentPaidShiftsPage - 1);
                              }
                            : undefined,
                        onNext:
                          hasMorePaidShiftsPages
                            ? () => {
                                void fetchPaidShiftsPageData(currentPaidShiftsPage + 1);
                              }
                            : undefined,
                      })}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fermer
        </Button>
      </DialogActions>

      <Dialog open={resetDialogOpen} onClose={resetLoading ? undefined : closeResetDialog}>
        <DialogTitle>Réinitialiser le compteur</DialogTitle>
        <DialogContent>
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          <Typography>
            Confirmer la réinitialisation de <strong>{resetTarget?.label}</strong> pour le livreur
            <strong>{` ${driver.firstName} ${driver.lastName}`}</strong> ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cette action mettra le compteur à 0.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResetDialog} disabled={resetLoading}>
            Annuler
          </Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained" disabled={resetLoading}>
            {resetLoading ? <CircularProgress size={18} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DriverDetailsDialog;
